import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import DropDown from "../../../../components/DropDown";
import InputField from "../../../../components/InputField";
import Icon from "../../../../components/Icon";
import { useEditDetails } from "./EditDetailsData";
import { usePermissions } from "../../../../utils/roleUtils";
import DocDeficiencyModal from "../DocDeficiencyModal";
import DocApproveModal from "../DocApproveModal";
import ViewLogsModal from "../ViewLogs";
import ApproveReviewModal from "../ApproveReviewModal";
import OtpVerificationModal from "../OtpVerificationModal";
import AadharEditModal from "../AadharEditModal";
import { getStateFromGST } from "../../../../constants/gstStateCodes";
import { uploadOnboardingDocumentAction } from "../../../../actions/uploadOnboardingDocumentAction";
import { useDocumentViewer } from "../../../../components/DocumentViewerModal/useDocumentViewer";
import {
  approveDocumentAction,
  resolveDocumentName,
} from "../../../../actions/approveDocumentAction";
import { downloadLedgerAction } from "../../../../actions/downloadLedgerAction";
import UtilityForm from "../../../UploadDetails/Components/UtilityForm";
import MSMESection from "../../../UploadDetails/Components/MSMESection";
import ThirdPartySection from "../../../UploadDetails/Components/ThirdPartySection";
import {
  PRIMARY_FIELDS,
  KYC_ENTITY_FIELDS,
  KYC_GST_FIELDS,
  KYC_MSME_FIELDS,
  KYC_MSME_DETAIL_FIELDS,
  KYC_CONTACT_FIELDS,
  KYC_BANK_FIELDS,
  DOCS_BY_ENTITY_TYPE,
  DOCS_BANK,
} from "../../AllDataFields";
import { getUserData } from "../../../../utils/authnicate";
import { resendKycLinkAction } from "../../../../actions/resendKycLinkAction";
import { isPanNumberField } from "../../../../utils/panFieldUtils";
import UploadedDocumentsView from "../UploadedDocumentsView";
import "./index.scss";

// ── GST Entries — editable (add/remove rows) or read-only view ───────────────
const GSTEntriesModal = ({
  isGSTRegistered,
  gstEntries,
  onAdd,
  onRemove,
  onChange,
  readOnly,
  documents,
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies,
  isVerified = false,
}) => {
  const isRegistered = isGSTRegistered === true || isGSTRegistered === "yes";

  // Local state to track which GST file the deficiency modal is open for
  const [gstDefTarget, setGstDefTarget] = React.useState(null); // { doc, file }

  // Find existing deficiency reason for a GST file
  const getExistingGstDef = (docKey, file) =>
    docDeficiencies.find(
      (d) => d.docName === (file.fileName || "GST Certificate"),
    )?.reason || "";

  if (!isRegistered) return null;

  return (
    <>
      <div className="gst-entries-modal">
        {gstEntries.map((entry, idx) => {
          // Find the uploaded GST cert for this entry by index key
          const certDoc = documents.find((d) => d.key === `gst_file_${idx}`);
          const certFiles = certDoc?.files?.filter((f) => f.isUploaded) || [];
          const hasCert = certFiles.length > 0;
          return (
            <div key={idx} className="gst-entry-row">
              <div className="gst-entry-row__header">
                <span className="gst-entry-row__index">GST #{idx + 1}</span>
                {!readOnly && gstEntries.length > 1 && (
                  <button
                    type="button"
                    className="btn-remove-gst"
                    onClick={() => onRemove(idx)}
                    title="Remove this GST entry"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="gst-entry-row__fields">
                <InputField
                  name={`gstNumber_${idx}`}
                  label="GST Number"
                  type="text"
                  placeholder="e.g. 22AAAAA0000A1Z5"
                  value={entry.gstNumber || ""}
                  maxLength={15}
                  onChange={undefined}
                  readOnly={true}
                  required
                />
                <InputField
                  name={`gstState_${idx}`}
                  label="State"
                  type="text"
                  placeholder="Auto-filled from GST number"
                  value={entry.gstState || entry.state || ""}
                  readOnly
                />
                <InputField
                  name={`gstAddress_${idx}`}
                  label="GST Address"
                  type="text"
                  placeholder="Enter GST address"
                  value={entry.gstAddress || ""}
                  onChange={
                    readOnly
                      ? undefined
                      : (e) => onChange(idx, "gstAddress", e.target.value)
                  }
                  readOnly={readOnly}
                />

                {/* GST Certificate — 4th column, same row */}
                <div
                  className={`doc-slot ${
                    hasCert
                      ? certDoc?.isDeficient
                        ? "doc-slot--deficient"
                        : "doc-slot--done"
                      : "doc-slot--pending"
                  }`}
                >
                  <div className="doc-slot__header">
                    <span className="doc-slot__icon">
                      {hasCert ? "✓" : "○"}
                    </span>
                    <span className="doc-slot__name">GST Certificate</span>
                    {!hasCert && (
                      <span className="doc-status pending">Pending</span>
                    )}
                  </div>
                  {hasCert &&
                    certFiles.map((file, fi) => {
                      const doc = {
                        key: `gst_file_${idx}`,
                        label: "GST Certificate",
                        gstId: entry.gstId ?? 0,
                        deficiencyIn: "isGSTDef",
                        isApproved: certDoc?.isApproved || false,
                        isDeficient: certDoc?.isDeficient || false,
                      };
                      // existing reason from locally-added deficiencies OR from API (isDeficient flag)
                      const existing = getExistingGstDef(doc.key, file);
                      // show active dot if there's a local reason OR the API already flagged it
                      const hasDeficiency = !!(existing || doc.isDeficient);
                      return (
                        <div key={fi} className="doc-slot__file">
                          <span>📄 {file.fileName}</span>
                          <div className="doc-slot__actions">
                            <button
                              type="button"
                              className="view-doc-btn"
                              onClick={() =>
                                onView(file.filePath, file.fileName)
                              }
                              title="View"
                            >
                              <Icon iconName="view" width={16} height={16} />
                            </button>
                            {canDeficiency &&
                              !doc.isApproved &&
                              !isVerified && (
                                <button
                                  type="button"
                                  className={`doc-action-btn doc-action-btn--deficiency${hasDeficiency ? " doc-action-btn--active" : ""}`}
                                  onClick={() => setGstDefTarget({ doc, file })}
                                  title={
                                    existing
                                      ? `Edit deficiency: ${existing}`
                                      : doc.isDeficient
                                        ? "Deficiency already marked — click to update"
                                        : "Mark deficiency"
                                  }
                                >
                                  ✗
                                  {hasDeficiency && (
                                    <span className="doc-action-btn__dot" />
                                  )}
                                </button>
                              )}
                            {canDeficiency && doc.isApproved && !isVerified && (
                              <span
                                className="doc-slot__approved-lock"
                                title="Document already approved — deficiency not allowed"
                              >
                                🔒
                              </span>
                            )}
                            {canApprove && !isVerified && !doc.isDeficient && (
                              <button
                                type="button"
                                className="doc-action-btn doc-action-btn--approve"
                                onClick={() =>
                                  onDocApprove &&
                                  onDocApprove(
                                    {
                                      key: `gst_file_${idx}`,
                                      label: "GST Certificate",
                                      gstId: entry.gstId ?? 0,
                                    },
                                    file,
                                  )
                                }
                                title="Approve"
                              >
                                ✓
                              </button>
                            )}
                            {canApprove && !isVerified && doc.isDeficient && (
                              <span
                                className="doc-slot__approved-lock"
                                title="Document has a deficiency — resolve it before approving"
                              >
                                🔒
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          );
        })}

        {!readOnly && (
          <button type="button" className="btn-add-gst" onClick={onAdd}>
            + Add GST Entry
          </button>
        )}
      </div>

      {/* Deficiency modal for GST certificate - opens when deficiency icon is clicked */}
      <DocDeficiencyModal
        isOpen={!!gstDefTarget}
        docName={gstDefTarget?.file?.fileName || "GST Certificate"}
        existingReason={
          gstDefTarget
            ? getExistingGstDef(gstDefTarget.doc.key, gstDefTarget.file)
            : ""
        }
        onClose={() => setGstDefTarget(null)}
        onSubmit={(reason) => {
          if (gstDefTarget) {
            onDocDeficiency &&
              onDocDeficiency(gstDefTarget.doc, gstDefTarget.file, reason);
          }
          setGstDefTarget(null);
        }}
      />
    </>
  );
};
const renderField = (
  field,
  entity,
  handleChange,
  handleBlur,
  errors,
  readOnly,
  aadharVisibilityProps = {},
) => {
  const {
    name,
    type,
    label,
    options,
    optionsByEntityType,
    conditionalOn,
    conditionalValue,
    ...rest
  } = field;

  // Conditional visibility
  if (conditionalOn && entity[conditionalOn] !== conditionalValue) return null;

  // Check if this is an Aadhar field
  const isAadhar =
    aadharVisibilityProps.isAadharField &&
    aadharVisibilityProps.isAadharField(name);

  // Get value - use Aadhar display value if it's an Aadhar field
  let value;
  if (type === "checkbox") {
    value = entity[name] || false;
  } else {
    // Always show raw API value — no masking in the input field
    value = entity[name] || "";
  }

  const error = errors[name] || "";
  const panReadOnly = isPanNumberField(name);
  const fieldReadOnly = readOnly || panReadOnly;

  if (type === "select") {
    // Resolve options — use entity-type-specific options if available
    const resolvedOptions =
      optionsByEntityType?.[entity.entityType] ?? options ?? [];
    return (
      <div key={name} className="form-group">
        <label className="input-label" htmlFor={name}>
          {label}
          {rest.required && <span className="required-mark"> *</span>}
        </label>
        <DropDown
          name={name}
          value={value}
          onChange={fieldReadOnly ? undefined : handleChange}
          options={resolvedOptions}
          error={error}
          fullWidth
          disabled={fieldReadOnly}
        />
      </div>
    );
  }

  return (
    <InputField
      key={name}
      name={name}
      label={label}
      type={type || "text"}
      value={value}
      onChange={fieldReadOnly ? undefined : handleChange}
      onBlur={fieldReadOnly ? undefined : handleBlur}
      error={error}
      readOnly={fieldReadOnly && type !== "checkbox"}
      disabled={fieldReadOnly && type === "checkbox"}
      showEyeIcon={
        isAadhar && aadharVisibilityProps.isViewAdharNumber !== false
      }
      onEyeIconClick={
        isAadhar &&
        aadharVisibilityProps.isViewAdharNumber !== false &&
        aadharVisibilityProps.handleEyeIconClick
          ? () => aadharVisibilityProps.handleEyeIconClick(name)
          : undefined
      }
      isAadharVisible={
        isAadhar && aadharVisibilityProps.isAadharVisible
          ? aadharVisibilityProps.isAadharVisible(name)
          : false
      }
      {...rest}
    />
  );
};

const renderFields = (
  fields,
  entity,
  handleChange,
  handleBlur,
  errors,
  readOnly,
  aadharVisibilityProps = {},
) =>
  fields.map((f) => {
    return renderField(
      f,
      entity,
      handleChange,
      handleBlur,
      errors,
      readOnly,
      aadharVisibilityProps,
    );
  });

const DocSlot = ({
  doc,
  uploadedFiles = [],
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies = [],
  isVerified = false,
}) => {
  const hasFiles = uploadedFiles.length > 0;
  const [deficiencyTarget, setDeficiencyTarget] = React.useState(null);
  const [approveTarget, setApproveTarget] = React.useState(null);

  // find existing deficiency for a given file
  const getExisting = (file) =>
    docDeficiencies.find((d) => d.docName === (file.fileName || doc.label))
      ?.reason || "";

  return (
    <>
      <div
        className={`doc-slot ${hasFiles ? "doc-slot--done" : "doc-slot--pending"}`}
      >
        <div className="doc-slot__header">
          <span className="doc-slot__icon">{hasFiles ? "✓" : "○"}</span>
          <span className="doc-slot__name">{doc.label}</span>
          {doc.required && <span className="mandatory-badge">Required</span>}
          {!hasFiles && <span className="doc-status pending">Pending</span>}
        </div>

        {hasFiles &&
          uploadedFiles.map((file, i) => {
            const existing = getExisting(file);
            return (
              <div key={i} className="doc-slot__file">
                <span>📄 {file.fileName}</span>
                {file.uploadedOn && (
                  <span className="doc-slot__date">
                    {new Date(file.uploadedOn).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                )}
                <div className="doc-slot__actions">
                  <button
                    type="button"
                    className="view-doc-btn"
                    onClick={() => onView(file.filePath, file.fileName)}
                    title={`View ${file.fileName}`}
                  >
                    <Icon iconName="view" width={16} height={16} />
                  </button>
                  {/* create def icon here — hidden when entity is verified */}
                  {canDeficiency && !doc.isApproved && !isVerified && (
                    <button
                      type="button"
                      className={`doc-action-btn doc-action-btn--deficiency${existing ? " doc-action-btn--active" : ""}`}
                      onClick={() => setDeficiencyTarget(file)}
                      title={
                        existing
                          ? `Edit deficiency: ${existing}`
                          : "Mark deficiency"
                      }
                    >
                      ✗{existing && <span className="doc-action-btn__dot" />}
                    </button>
                  )}
                  {/* approved lock — hidden when entity is verified */}
                  {canDeficiency && doc.isApproved && !isVerified && (
                    <span
                      className="doc-slot__approved-lock"
                      title="Document already approved — deficiency not allowed"
                    >
                      🔒
                    </span>
                  )}
                  {/* approve icon — hidden when entity is verified or doc has deficiency */}
                  {canApprove && !isVerified && !doc.isDeficient && (
                    <button
                      type="button"
                      className="doc-action-btn doc-action-btn--approve"
                      onClick={() => setApproveTarget(file)}
                      title="Approve document"
                    >
                      ✓
                    </button>
                  )}
                  {/* deficiency lock on approve — doc has deficiency, cannot approve */}
                  {canApprove && !isVerified && doc.isDeficient && (
                    <span
                      className="doc-slot__approved-lock"
                      title="Document has a deficiency — resolve it before approving"
                    >
                      🔒
                    </span>
                  )}
                </div>
                {existing && (
                  <div className="doc-slot__deficiency-preview">
                    <span className="doc-slot__deficiency-label">
                      Deficiency:
                    </span>
                    <span className="doc-slot__deficiency-text">
                      {existing}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <DocDeficiencyModal
        isOpen={!!deficiencyTarget}
        docName={deficiencyTarget?.fileName || ""}
        existingReason={deficiencyTarget ? getExisting(deficiencyTarget) : ""}
        onClose={() => setDeficiencyTarget(null)}
        onSubmit={(reason) => {
          onDocDeficiency && onDocDeficiency(doc, deficiencyTarget, reason);
          setDeficiencyTarget(null);
        }}
      />
      <DocApproveModal
        isOpen={!!approveTarget}
        docName={approveTarget?.fileName || ""}
        onClose={() => setApproveTarget(null)}
        onConfirm={() => {
          onDocApprove && onDocApprove(doc, approveTarget);
          setApproveTarget(null);
        }}
      />
    </>
  );
};

const DocSection = ({
  title,
  docs,
  documents,
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies,
  entity = {},
  handleChange,
  handleBlur,
  errors = {},
  readOnly = true,
  aadharVisibilityProps = {},
  isVerified = false,
  resolveUploadedFiles,
}) => {
  // State for the Aadhar edit modal
  const [aadharEditField, setAadharEditField] = React.useState(null); // fieldName being edited
  const [aadharEditLabel, setAadharEditLabel] = React.useState("");

  if (!docs || docs.length === 0) return null;
  return (
    <>
      <div className="doc-inline-section">
        {title && <p className="doc-inline-section__title">{title}</p>}
        <div className="doc-inline-grid">
          {docs.map((doc) => {
            const matched =
              documents.find((d) => d.key === doc.key) ||
              documents.find((d) =>
                d.documentName
                  ?.toLowerCase()
                  .includes(doc.label.toLowerCase().split(" ")[0]),
              );
            const uploadedFiles = resolveUploadedFiles
              ? resolveUploadedFiles(doc, matched)
              : matched?.files?.filter((f) => f.isUploaded) || [];
            const hasUploadedDoc = uploadedFiles.length > 0;

            // Determine if this input field is an Aadhar field with an uploaded doc
            const fieldName = doc.inputField?.name;
            const panReadOnly = isPanNumberField(fieldName);
            const docFieldReadOnly = readOnly || panReadOnly;
            const isAadhar =
              fieldName &&
              hasUploadedDoc &&
              aadharVisibilityProps.isAadharField &&
              aadharVisibilityProps.isAadharField(fieldName);

            // Get display value — always show raw API value in the input field
            // Eye icon is available for OTP verification if needed
            const inputVal = fieldName ? entity[fieldName] || "" : "";

            return (
              <div key={doc.key} className="doc-section-row">
                {/* Input field for the document number — same row as KYC form */}
                {doc.inputField && (
                  <InputField
                    name={fieldName}
                    label={`${doc.label} Number`}
                    type="text"
                    value={inputVal}
                    onChange={docFieldReadOnly ? undefined : handleChange}
                    onBlur={docFieldReadOnly ? undefined : handleBlur}
                    placeholder={doc.inputField.placeholder}
                    maxLength={doc.inputField.maxLength}
                    error={errors?.[fieldName] || ""}
                    readOnly={docFieldReadOnly}
                    // Eye icon — only for Aadhar fields with permission
                    showEyeIcon={
                      !!isAadhar &&
                      aadharVisibilityProps.isViewAdharNumber !== false
                    }
                    onEyeIconClick={
                      isAadhar &&
                      aadharVisibilityProps.isViewAdharNumber !== false &&
                      aadharVisibilityProps.handleEyeIconClick
                        ? () =>
                            aadharVisibilityProps.handleEyeIconClick(fieldName)
                        : undefined
                    }
                    isAadharVisible={
                      isAadhar && aadharVisibilityProps.isAadharVisible
                        ? aadharVisibilityProps.isAadharVisible(fieldName)
                        : false
                    }
                    // Edit icon — only for Aadhar fields with permission
                    showEditIcon={
                      !!isAadhar &&
                      aadharVisibilityProps.isEditAdharNumber !== false
                    }
                    onEditIconClick={
                      isAadhar &&
                      aadharVisibilityProps.isEditAdharNumber !== false
                        ? () => {
                            setAadharEditField(fieldName);
                            setAadharEditLabel(doc.label + " Number");
                          }
                        : undefined
                    }
                  />
                )}
                <DocSlot
                  doc={{
                    ...doc,
                    isApproved: matched?.isApproved || false,
                    isDeficient: matched?.isDeficient || false,
                  }}
                  uploadedFiles={uploadedFiles}
                  onView={onView}
                  canApprove={canApprove}
                  canDeficiency={canDeficiency}
                  onDocApprove={onDocApprove}
                  onDocDeficiency={onDocDeficiency}
                  docDeficiencies={docDeficiencies}
                  isVerified={isVerified}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Aadhar Edit Modal — opens when edit icon is clicked on an Aadhar field */}
      <AadharEditModal
        isOpen={!!aadharEditField}
        onClose={() => {
          setAadharEditField(null);
          setAadharEditLabel("");
        }}
        onSuccess={(newValue) => {
          if (aadharEditField && handleChange) {
            handleChange({
              target: { name: aadharEditField, value: newValue, type: "text" },
            });
          }
          setAadharEditField(null);
          setAadharEditLabel("");
        }}
        adminEmail={aadharVisibilityProps.adminEmail || ""}
        fieldKey={aadharEditField || ""}
        fieldLabel={aadharEditLabel}
        existingValue={aadharEditField ? entity[aadharEditField] || "" : ""}
        entityPrimaryId={entity.entityPrimaryId || entity.id || 0}
      />
    </>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const EditClientModal = ({
  isOpen,
  onClose,
  clientData,
  onSave,
  onApprove,
  onDeficiency,
}) => {
  const {
    formData: entity,
    errors,
    isSubmitting,
    departmentOptions,
    parentOptions,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    showThirdParty,
    handleChange,
    handleBlur,
    handleUpdate,
    handleClose,
    // Aadhar visibility
    otpModalOpen,
    currentAadharField,
    adminEmail,
    entityPrimaryId,
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
  } = useEditDetails(clientData, onSave, onClose);

  const {
    canApprove,
    canDeficiency,
    isReadOnly,
    canEdit,
    canUpdate,
    canViewlogs,
    isFinance,
    isViewAdharNumber,
    isEditAdharNumber,
  } = usePermissions();

  // Aadhar visibility props — built after usePermissions so permission flags are available
  const aadharVisibilityProps = {
    handleEyeIconClick,
    isAadharVisible,
    getAadharDisplayValue,
    isAadharField,
    adminEmail,
    isViewAdharNumber: isViewAdharNumber !== false,
    isEditAdharNumber: isEditAdharNumber !== false,
  };

  const [isEditing, setIsEditing] = useState(!clientData?.viewOnly && canEdit);

  useEffect(() => {
    setIsEditing(!clientData?.viewOnly && canEdit);
  }, [clientData, canEdit]);

  const effectiveReadOnly = isReadOnly || !isEditing;

  // ── Local GST entries state (seeded from entity.gstList) ─────────────────
  const [localGstEntries, setLocalGstEntries] = useState([]);

  useEffect(() => {
    const list = entity.gstList;
    if (Array.isArray(list) && list.length > 0) {
      setLocalGstEntries(
        list.map((g) => ({
          gstNumber: g.gstNumber || "",
          gstState: g.state || "",
          state: g.state || "",
          gstAddress: g.gstAddress || "",
          gstId: g.gstId ?? g.id ?? 0,
          gstCertificatePath: g.gstCertificatePath || "",
          // preserve approval/deficiency flags from API
          isGST: g.isGST === true,
          isGSTDef: g.isGSTDef === true,
        })),
      );
    } else if (!effectiveReadOnly) {
      setLocalGstEntries([
        {
          gstNumber: "",
          gstState: "",
          gstAddress: "",
          gstId: 0,
          gstCertificatePath: "",
          isGST: false,
          isGSTDef: false,
        },
      ]);
    }
  }, [entity.gstList, effectiveReadOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGstAdd = () =>
    setLocalGstEntries((prev) => [
      ...prev,
      { gstNumber: "", gstState: "", gstAddress: "" },
    ]);

  const handleGstRemove = (idx) =>
    setLocalGstEntries((prev) => prev.filter((_, i) => i !== idx));

  const handleGstChange = (idx, field, value) => {
    setLocalGstEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== idx) return entry;
        const updated = {
          ...entry,
          [field]: field === "gstNumber" ? value.toUpperCase() : value,
        };
        if (field === "gstNumber") {
          updated.gstState = getStateFromGST(value.toUpperCase());
          updated.state = updated.gstState;
        }
        return updated;
      }),
    );
  };

  // saved per-doc deficiencies: [{ docName, reason }]
  const [docDeficiencies, setDocDeficiencies] = useState([]);
  const [deficiencyReviewOpen, setDeficiencyReviewOpen] = useState(false);
  const [approveReviewOpen, setApproveReviewOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  // Tracks if requireKYC was toggled to true in THIS session (not from API)
  const [kycJustToggled, setKycJustToggled] = useState(false);
  // View Logs modal
  const [viewLogsOpen, setViewLogsOpen] = useState(false);

  // Track which file paths have been viewed
  const [viewedFilePaths, setViewedFilePaths] = useState(new Set());
  // Track which file paths have been individually approved (✓ per doc)
  const [approvedFilePaths, setApprovedFilePaths] = useState(new Set());
  // Newly uploaded doc paths — keyed by documentType, merged into update payload
  const [uploadedPaths, setUploadedPaths] = useState({});

  const {
    viewDocument,
    closeViewer: closeDocumentViewer,
    DocumentViewer,
  } = useDocumentViewer(entity.entityPrimaryId || entity.id || 0);

  // reset when modal closes/opens
  useEffect(() => {
    if (!isOpen) {
      setDocDeficiencies([]);
      setViewedFilePaths(new Set());
      setApprovedFilePaths(new Set());
      setApproveReviewOpen(false);
      setKycJustToggled(false);
      setUploadedPaths({});
      closeDocumentViewer();
    }
  }, [isOpen, closeDocumentViewer]);

  // Seed approvedFilePaths + viewedFilePaths from API data when modal opens.
  // Any doc where isApproved === true (isXxx flag from get-entities) is pre-approved.
  useEffect(() => {
    if (!isOpen || !clientData?.documents) return;
    const preApproved = new Set();
    const preViewed = new Set();
    clientData.documents.forEach((doc) => {
      if (doc.isApproved) {
        doc.files?.forEach((f) => {
          if (f.filePath) {
            preApproved.add(f.filePath);
            preViewed.add(f.filePath); // approved implies viewed
          }
        });
      }
    });
    setApprovedFilePaths(preApproved);
    setViewedFilePaths((prev) => new Set([...prev, ...preViewed]));
  }, [isOpen, clientData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Upload a file and store the returned path
  const handleDocUpload = async (documentType, file) => {
    if (!file) return;
    const entityId = entity.entityId || "";
    const result = await uploadOnboardingDocumentAction({
      entityId,
      id: 0,
      documentType,
      fileName: file.name.replace(/\.[^/.]+$/, ""),
      file,
    });
    if (result.success && result.data?.filePath) {
      setUploadedPaths((prev) => ({
        ...prev,
        [documentType]: result.data.filePath,
      }));
      toast.success(`${documentType.replace(/Path$/, "")} uploaded`);
    } else if (!result.success) {
      toast.error(`Upload failed: ${result.error || documentType}`);
    }
  };

  const handleViewDocument = async (filePath, displayName) => {
    await viewDocument(filePath, {
      displayName,
      onSuccess: (path) =>
        setViewedFilePaths((prev) => new Set([...prev, path])),
    });
  };

  // called from DocSlot — saves deficiency locally
  const handleDocDeficiency = (doc, file, reason) => {
    const displayName = doc.documentName || doc.label || file.fileName || "";

    // Block deficiency if the entity is already fully approved
    if (entity.isVerified === true) {
      toast.error(
        "This entity is already approved — deficiency cannot be raised.",
      );
      return;
    }

    // Block deficiency if the document is already approved
    if (doc.isApproved) {
      toast.error(
        `"${displayName}" is already approved and cannot be marked as deficient.`,
      );
      return;
    }

    // Priority 1: use flagName from entity.deficiencyMap (populated from get-entities *Def flags)
    // Priority 2: fall back to static map
    const entityDefMap = entity.deficiencyMap || {};
    const STATIC_DEF_FLAG_NAMES = {
      doc_aadhar: "isAadharCardDef",
      doc_pan: "isPANCardDef",
      doc_partnershipDeed: "isPartnershipDeedDef",
      doc_aadharBoth: "isAadharOfBothPartnersDef",
      doc_aadharBothSecondary: "isAadharOfBothPartnersDefSecond",
      doc_llpCertificate: "isLLPCertificateDef",
      doc_coi: "isCertificateOfIncorporationDef",
      doc_directorPan: "isDirectorPanDef",
      doc_directorAadhar: "isDirectorAadharDef",
      doc_trustDeed: "isTrustDeedDef",
      doc_panTrust: "isPANOfTrustDef",
      doc_panTrustee: "isPANOfTrusteeDef",
      doc_aadharTrustee: "isAadharOfTrusteeDef",
      doc_msme_certificate: "isMSMECertificateDef",
      doc_utility_bill: "isUtilityBillDocumentDef",
      doc_bank_proof: "isPassbookOrCancelledChequeDef",
      third_party_file: "isSignedFormDef",
    };
    // gst_file_0, gst_file_1, etc. all map to isGSTDef
    const staticFlag = /^gst_file_\d+$/.test(doc.key)
      ? "isGSTDef"
      : STATIC_DEF_FLAG_NAMES[doc.key];
    const deficiencyIn =
      doc.deficiencyIn ||
      entityDefMap[doc.key]?.flagName ||
      staticFlag ||
      displayName;

    setDocDeficiencies((prev) => {
      const filtered = prev.filter((d) => d.docName !== displayName);
      return [
        ...filtered,
        {
          docName: displayName,
          fileName: displayName,
          filePath: file.filePath || "",
          reason,
          deficiencyIn,
          gstId: doc.gstId ?? 0,
        },
      ];
    });
    toast.info(`Deficiency saved for "${displayName}"`);
  };

  const handleDocApprove = async (doc, file) => {
    // Block approval if the document has a deficiency
    if (doc.isDeficient) {
      toast.error(
        `"${doc.documentName || doc.label}" has a deficiency and cannot be approved. Please resolve the deficiency first.`,
      );
      return;
    }

    const entityPrimaryId = entity.entityPrimaryId || entity.id || 0;
    const documentName = resolveDocumentName(doc.key);
    const approvedBy = entity.modifiedBy || entity.createdBy || "Admin";

    // Pass gstId for GST certificate docs, null for all other documents
    const isGstDoc = /^gst_file_\d+$/.test(doc.key);
    const gstId = isGstDoc ? (doc.gstId ?? null) : null;

    const result = await approveDocumentAction({
      entityPrimaryId,
      gstId,
      documentName,
      approvedBy,
    });

    if (result.success) {
      setApprovedFilePaths((prev) => new Set([...prev, file.filePath]));
      toast.success(`"${file.fileName || doc.label}" approved`);
    } else {
      toast.error(
        result.message ||
          `Failed to approve "${doc.documentName || doc.label}"`,
      );
    }
  };

  // main Deficiency button → open review modal
  const handleDeficiencyButtonClick = () => {
    if (entity.isVerified === true) {
      toast.error(
        "This entity is already approved — deficiency cannot be raised.",
      );
      return;
    }
    setDeficiencyReviewOpen(true);
  };

  // from review modal → call API
  const handleDeficiencySubmitToApi = async () => {
    if (!onDeficiency) return;
    if (docDeficiencies.length === 0) {
      toast.error("No deficiencies added. Please mark at least one document.");
      return;
    }
    setIsActionLoading(true);
    try {
      const email = (entity.emailAddress || "").trim().split(/\s+/)[0];
      const spoc = (entity.spocEmailAddress || "").trim().split(/\s+/)[0];

      // Get logged-in user email for defCreatedBy
      const loggedInUser = getUserData();
      const defCreatedBy = loggedInUser?.email || "";
      // console.log("USER DETAILS LOGGEDIN :", loggedInUser?.email);

      // Build structured deficiencies array — one entry per document
      const deficiencies = docDeficiencies.map((d) => ({
        fileName: d.fileName || d.docName || "",
        filePath: d.filePath || "",
        descriptions: d.reason || "",
        deficiencyIn: d.deficiencyIn || d.fileName || d.docName || "",
        gstId: d.gstId ?? 0,
      }));

      await onDeficiency({
        entityPrimaryId: entity.entityPrimaryId || entity.id || 0,
        entityId: entity.entityId || "",
        entityName: entity.entityName || entity.EntityName || "",
        emailAddress: email,
        spocEmail: spoc,
        deficiencies,
        defCreatedBy,
      });

      setDeficiencyReviewOpen(false);
      setDocDeficiencies([]);
      handleClose();
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleApproveClick = () => {
    // Open the approve review modal — user must approve each doc there
    setApproveReviewOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!onApprove) return;
    setIsActionLoading(true);
    try {
      await onApprove({
        entityPrimaryId: entity.entityPrimaryId || entity.id,
        email: (entity.emailAddress || entity.contactPersonEmail || "")
          .trim()
          .split(/\s+/)[0],
        spocEmail: (entity.spocEmailAddress || "").trim().split(/\s+/)[0],
      });
      setApproveReviewOpen(false);
      handleClose();
    } finally {
      setIsActionLoading(false);
    }
  };

  if (!clientData) return null;

  const documents = clientData.documents || [];

  const isMsmeRegistered =
    entity.isMSMERegistered === "yes" ||
    entity.isMSMERegistered === true ||
    entity.isRegisteredUnderMSME === true;

  const getMsmeUploadedFiles = () => {
    const matched = documents.find((d) => d.key === "doc_msme_certificate");
    const fromDocs = matched?.files?.filter((f) => f.isUploaded) || [];
    if (fromDocs.length > 0) return fromDocs;
    const path = entity.msmeCertificatePath;
    if (path && path !== "string") {
      return [
        {
          isUploaded: true,
          fileName: path.split(/[/\\]/).pop() || "MSME Certificate",
          filePath: path,
        },
      ];
    }
    return [];
  };

  const msmeUploadedFiles = getMsmeUploadedFiles();
  const hasMsmeUploaded = msmeUploadedFiles.length > 0;
  const showMsmeDocuments = isMsmeRegistered || hasMsmeUploaded;

  const getMsmeExistingFile = () => {
    const first = msmeUploadedFiles[0];
    if (!first) return null;
    return { fileName: first.fileName, filePath: first.filePath };
  };

  // Check if ANY document has a deficiency — blocks final approval entirely
  const hasAnyDeficiency = documents.some((d) => d.isDeficient === true);

  // Non-deficient uploaded docs — used for the per-doc approval checks
  const approvableDocs = documents.filter((d) => !d.isDeficient);

  // All uploaded file paths from approvable docs — used to gate the Approve button
  const allUploadedFilePaths = approvableDocs
    .flatMap((d) => d.files?.filter((f) => f.isUploaded && f.filePath) || [])
    .map((f) => f.filePath);

  // Count approvable docs that have NO uploaded files (pending)
  const pendingDocCount = approvableDocs.filter(
    (d) => !d.files?.some((f) => f.isUploaded),
  ).length;

  // Approve enabled only when:
  // 1. No document has a deficiency
  // 2. There are uploaded approvable docs
  // 3. No pending (not-yet-uploaded) approvable docs
  // 4. Every uploaded approvable file has been viewed AND individually approved
  const allDocsViewed =
    allUploadedFilePaths.length > 0 &&
    allUploadedFilePaths.every((p) => viewedFilePaths.has(p));
  const allDocsApproved =
    allUploadedFilePaths.length > 0 &&
    allUploadedFilePaths.every((p) => approvedFilePaths.has(p));
  const approveReady =
    !hasAnyDeficiency &&
    pendingDocCount === 0 &&
    allDocsViewed &&
    allDocsApproved;

  const resolvedPrimaryFields = PRIMARY_FIELDS.map((f) => {
    if (f.name === "aaDepartmentFunction")
      return { ...f, options: departmentOptions };
    if (f.name === "parentCode") return { ...f, options: parentOptions };
    return f;
  });

  const entityTypeDocs = DOCS_BY_ENTITY_TYPE[entity.entityType] || [];

  const headerAction = canEdit ? (
    <>
      <div className="icon-header-all-list-page">
        <Icon
          iconName={isEditing ? "view" : "edit"}
          className="modal-edit-toggle"
          onClick={() => setIsEditing((prev) => !prev)}
        />
        <button className="modal-close" onClick={handleClose}>
          &times;
        </button>
      </div>
    </>
  ) : (
    <button className="modal-close" onClick={handleClose}>
      &times;
    </button>
  );

  const docSectionProps = {
    documents,
    onView: handleViewDocument,
    canApprove,
    canDeficiency,
    onDocApprove: handleDocApprove,
    onDocDeficiency: handleDocDeficiency,
    docDeficiencies,
    entity,
    handleChange: effectiveReadOnly ? undefined : handleChange,
    handleBlur: effectiveReadOnly ? undefined : handleBlur,
    errors,
    readOnly: effectiveReadOnly,
    aadharVisibilityProps,
    isVerified: entity.isVerified === true,
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Entity Details"
        editViewModal="true"
        filledBy={entity.kycCreatedBy || entity.kycCreatedBy || ""}
        size="fullscreen"
        style={{
          height: "100vh",
          width: "100vw",
        }}
        entityID={entity.entityId || entity.entityPrimaryId || entity.id || ""}
        headerAction={headerAction}
        height="100vh"
        width="100vw"
      >
        <form className="edit-entity-form">
          <div className="form-content">
            {/* 1 ── Primary Details — always visible */}
            <div className="form-section">
              <h3 className="section-title">Primary Details</h3>
              <div className="form-grid">
                {renderFields(
                  resolvedPrimaryFields,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                  aadharVisibilityProps,
                )}
              </div>

              {/* Require KYC toggle — always visible, calls update API on change */}
              <div className="require-kyc-row">
                <div
                  className="form-group"
                  style={{ maxWidth: 260, marginTop: 12 }}
                >
                  <label className="input-label">Require KYC</label>
                  <DropDown
                    name="requireKYC"
                    value={entity.requireKYC ? "true" : "false"}
                    onChange={async (e) => {
                      const newVal = e.target.value === "true";
                      const synth = {
                        target: {
                          name: "requireKYC",
                          value: newVal,
                          type: "text",
                        },
                      };
                      handleChange(synth);
                      if (newVal) setKycJustToggled(true);
                      else setKycJustToggled(false);

                      if (newVal) {
                        // No → Yes: ONLY call resend-kyc-link, no update-entity
                        const entityPrimaryId =
                          entity.entityPrimaryId || entity.id;
                        if (entityPrimaryId) {
                          // console.log(
                          //   "[RequireKYC] No→Yes — calling resend-kyc-link only, id:",
                          //   entityPrimaryId,
                          // );
                          try {
                            const resendResult =
                              await resendKycLinkAction(entityPrimaryId);
                            // console.log(
                            //   "[RequireKYC] resend result:",
                            //   resendResult,
                            // );
                            if (resendResult.success) {
                              toast.success(
                                resendResult.message ||
                                  "KYC link sent successfully!",
                              );
                            } else {
                              toast.error(
                                resendResult.message ||
                                  "Failed to send KYC link",
                              );
                            }
                          } catch (err) {
                            console.error("[RequireKYC] resend error:", err);
                            toast.error("Failed to send KYC link");
                          }
                        }
                      }
                      // Yes → No: no API call here — update-entity fires on Update button click
                    }}
                    options={[
                      { value: "false", label: "No — KYC not required" },
                      { value: "true", label: "Yes — KYC required" },
                    ]}
                    disabled={effectiveReadOnly || entity.requireKYC === true}
                    fullWidth
                  />
                </div>
                {!entity.requireKYC && (
                  <p
                    className="require-kyc-notice__text"
                    style={{ marginTop: 8 }}
                  >
                    KYC is not required for this entity. Change to "Yes" to view
                    and fill KYC details.
                  </p>
                )}
                {kycJustToggled && entity.requireKYC && (
                  <p
                    className="require-kyc-notice__text"
                    style={{
                      marginTop: 8,
                      color: "#16a34a",
                      background: "#f0fdf4",
                      borderColor: "#bbf7d0",
                    }}
                  >
                    ✓ KYC requirement saved. Close and reopen this record to
                    fill in KYC details.
                  </p>
                )}
              </div>
            </div>

            {/* 2–6 ── KYC sections:
                - Show when requireKYC was true FROM THE API (clientData.entity.requireKYC)
                - Hide when user just toggled it in this session (kycJustToggled)
                  → they need to reopen the modal after saving to see the full form
                - When isSaveAsDraft is true, sections show but inputs are blank */}
            {entity.requireKYC && !kycJustToggled && (
              <>
                {/* 2 ── KYC Information */}
                <div className="form-section">
                  <h3 className="section-title">KYC Information</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_ENTITY_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                      aadharVisibilityProps,
                    )}
                  </div>
                  <DocSection
                    title={
                      entity.entityType
                        ? `Documents — ${entity.entityType.replace(/_/g, " ")}`
                        : null
                    }
                    docs={entityTypeDocs}
                    {...docSectionProps}
                  />
                </div>

                {/* 3 ── Contact Person Details */}
                <div className="form-section">
                  <h3 className="section-title">Contact Person Details</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_CONTACT_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                      aadharVisibilityProps,
                    )}
                  </div>
                </div>

                {/* 4 ── GST Information
                     folderNo exists → only show if isGSTRegistered=yes AND gstList has data */}
                {(!entity.folderNo ||
                  ((entity.isGSTRegistered === true ||
                    entity.isGSTRegistered === "yes") &&
                    Array.isArray(entity.gstList) &&
                    entity.gstList.length > 0)) && (
                  <div className="form-section">
                    <h3 className="section-title">GST Information</h3>
                    <div className="form-grid">
                      {renderFields(
                        KYC_GST_FIELDS,
                        entity,
                        handleChange,
                        handleBlur,
                        errors,
                        effectiveReadOnly,
                        aadharVisibilityProps,
                      )}
                    </div>

                    {/* GST registered — editable entries or read-only view */}
                    {(entity.isGSTRegistered === true ||
                      entity.isGSTRegistered === "yes") && (
                      <GSTEntriesModal
                        isGSTRegistered={entity.isGSTRegistered}
                        gstEntries={localGstEntries}
                        onAdd={handleGstAdd}
                        onRemove={handleGstRemove}
                        onChange={handleGstChange}
                        readOnly={effectiveReadOnly}
                        documents={documents}
                        onView={handleViewDocument}
                        canApprove={canApprove}
                        canDeficiency={canDeficiency}
                        onDocApprove={handleDocApprove}
                        onDocDeficiency={handleDocDeficiency}
                        docDeficiencies={docDeficiencies}
                        isVerified={entity.isVerified === true}
                      />
                    )}

                    {/* GST not registered — fields + doc slot */}
                    {(entity.isGSTRegistered === false ||
                      entity.isGSTRegistered === "no") && (
                      <>
                        {/* Edit mode: UtilityForm has fields + upload zone */}
                        {!effectiveReadOnly && (
                          <UtilityForm
                            isGSTRegistered="no"
                            formData={entity}
                            errors={errors}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            utilityFile={utilityFile}
                            handleUtilityFileChange={(file) => {
                              setUtilityFile(file);
                              handleDocUpload("UtilityBillDocumentPath", file);
                            }}
                            readOnly={false}
                          />
                        )}
                        {/* Read-only mode: show utility fields read-only */}
                        {effectiveReadOnly && (
                          <UtilityForm
                            isGSTRegistered="no"
                            formData={entity}
                            errors={{}}
                            handleChange={undefined}
                            handleBlur={undefined}
                            utilityFile={null}
                            handleUtilityFileChange={() => {}}
                            readOnly={true}
                          />
                        )}
                        {/* Doc slot — always shown for view/approve/deficiency */}
                        <DocSection
                          title="Utility Bill (Address Proof)"
                          docs={[
                            {
                              key: "doc_utility_bill",
                              label: "Utility Bill (Address Proof)",
                              required: true,
                            },
                          ]}
                          {...docSectionProps}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* 5 ── MSME Information */}
                <div className="form-section">
                  <h3 className="section-title">MSME Information</h3>
                  <div className="form-grid">
                    {renderFields(
                      KYC_MSME_FIELDS,
                      entity,
                      handleChange,
                      handleBlur,
                      errors,
                      effectiveReadOnly,
                      aadharVisibilityProps,
                    )}
                    {/* msmeStatus + other detail fields — shown when MSME is yes, or when a value already exists */}
                    {(entity.isMSMERegistered === "yes" ||
                      !!entity.msmeStatus) &&
                      renderFields(
                        KYC_MSME_DETAIL_FIELDS,
                        entity,
                        handleChange,
                        handleBlur,
                        errors,
                        effectiveReadOnly,
                        aadharVisibilityProps,
                      )}
                  </div>
                  {/* MSME certificate — when registered or already uploaded; hidden for draft KYC */}
                  {showMsmeDocuments && !entity.isSaveAsDraft && (
                    <>
                      {/* Edit: single block — MSME number + upload / existing file */}
                      {!effectiveReadOnly && (
                        <MSMESection
                          isMSMERegistered={
                            isMsmeRegistered || hasMsmeUploaded
                              ? "yes"
                              : entity.isMSMERegistered
                          }
                          formData={entity}
                          errors={errors}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          msmeFile={msmeFile}
                          handleMsmeFileChange={(file) => {
                            setMsmeFile(file);
                            handleDocUpload("MSMECertificatePath", file);
                          }}
                          existingMsmeFile={getMsmeExistingFile()}
                          readOnly={false}
                          onViewExisting={
                            getMsmeExistingFile()
                              ? () => {
                                  const f = getMsmeExistingFile();
                                  handleViewDocument(f.filePath, f.fileName);
                                }
                              : undefined
                          }
                        />
                      )}
                      {/* View: single block — number + uploaded doc (approve / deficiency) */}
                      {effectiveReadOnly && (
                        <DocSection
                          title="MSME Documents"
                          docs={[
                            {
                              key: "doc_msme_certificate",
                              label: "MSME Registration Certificate",
                              required: true,
                              inputField: {
                                name: "msmeNumber",
                                placeholder: "MSME Registration Number",
                                maxLength: 19,
                              },
                            },
                          ]}
                          resolveUploadedFiles={() => getMsmeUploadedFiles()}
                          {...docSectionProps}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* 6 ── Bank Details
                     folderNo exists → only show if hasBankDetails=yes AND has bank data */}
                {(!entity.folderNo ||
                  (entity.hasBankDetails === "yes" &&
                    (entity.bankName ||
                      entity.accountNumber ||
                      entity.ifscCode ||
                      entity.beneficiaryName ||
                      entity.branchName))) && (
                  <div className="form-section">
                    <h3 className="section-title">Bank Details</h3>

                    {/* hasBankDetails dropdown — always visible */}
                    <div className="form-grid" style={{ marginBottom: 12 }}>
                      <div className="form-group">
                        <label className="input-label" htmlFor="hasBankDetails">
                          Provide Bank Details?
                        </label>
                        <DropDown
                          name="hasBankDetails"
                          value={entity.hasBankDetails || ""}
                          onChange={
                            effectiveReadOnly ? undefined : handleChange
                          }
                          options={[
                            { value: "", label: "Select" },
                            { value: "yes", label: "Yes" },
                            { value: "no", label: "No" },
                          ]}
                          disabled={effectiveReadOnly}
                          fullWidth
                        />
                      </div>
                    </div>

                    {/* All other bank fields + docs — only when hasBankDetails === "yes" */}
                    {entity.hasBankDetails === "yes" && (
                      <>
                        <div className="form-grid">
                          {renderFields(
                            KYC_BANK_FIELDS,
                            entity,
                            handleChange,
                            handleBlur,
                            errors,
                            effectiveReadOnly,
                            aadharVisibilityProps,
                          )}
                          {entity.thirdPartyPayment && (
                            <InputField
                              name="relationWithBeneficiary"
                              label="Relation with Beneficiary"
                              value={entity.relationWithBeneficiary || ""}
                              onChange={
                                effectiveReadOnly ? undefined : handleChange
                              }
                              fullWidth
                              readOnly={effectiveReadOnly}
                            />
                          )}
                        </div>

                        {/* Bank document slot — pre-populate with already-uploaded docs */}
                        <DocSection
                          title="Bank Documents"
                          docs={DOCS_BANK.map((doc) => {
                            // Attach any already-uploaded files from clientData.documents
                            const matched = documents.find(
                              (d) => d.key === doc.key,
                            );
                            const uploadedList =
                              matched?.files?.filter((f) => f.isUploaded) || [];
                            return {
                              ...doc,
                              uploadedList,
                              isApproved: matched?.isApproved || false,
                              isDeficient: matched?.isDeficient || false,
                            };
                          })}
                          {...docSectionProps}
                        />

                        {/* ThirdPartySection in edit mode, DocSection in view mode */}
                        {showThirdParty &&
                          (!effectiveReadOnly ? (
                            <ThirdPartySection
                              show={true}
                              formData={entity}
                              errors={errors}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              thirdPartyFile={thirdPartyFile}
                              handleThirdPartyFileChange={(file) => {
                                setThirdPartyFile(file);
                                handleDocUpload("UploadSignedFormPath", file);
                              }}
                            />
                          ) : (
                            <DocSection
                              title="Signed Form (Third Party)"
                              docs={[
                                {
                                  key: "third_party_file",
                                  label: "Signed Form (Third Party)",
                                  required: true,
                                },
                              ]}
                              {...docSectionProps}
                            />
                          ))}
                      </>
                    )}

                    {/* When hasBankDetails === "no" — show a notice */}
                    {entity.hasBankDetails === "no" && (
                      <p
                        style={{
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          marginTop: 8,
                          padding: "0.75rem 1rem",
                          background: "#f9fafb",
                          border: "1px dashed #d1d5db",
                          borderRadius: 6,
                        }}
                      >
                        Bank details are not provided for this entity.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* 7 ── Ledger Information (Finance Role Only) */}
            {isFinance && (
              <>
                {/* <div>
                  <button>Add new ledger</button>
                </div> */}
                <div className="form-section">
                  <h3 className="section-title">Ledger Details</h3>

                  {/* ── Ledger Names (from ledgerNameList) — table layout ── */}
                  {entity.ledgerNameList &&
                    entity.ledgerNameList.length > 0 && (
                      <div
                        style={{
                          marginBottom: "1.25rem",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "0.875rem",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                background: "#f9fafb",
                                borderBottom: "2px solid #e5e7eb",
                              }}
                            >
                              <th
                                style={{
                                  padding: "0.625rem 1rem",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                #
                              </th>
                              {/* <th
                                style={{
                                  padding: "0.625rem 1rem",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                Entity ID
                              </th> */}
                              <th
                                style={{
                                  padding: "0.625rem 1rem",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                Company Name
                              </th>
                              <th
                                style={{
                                  padding: "0.625rem 1rem",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                Ledger Name
                              </th>
                              <th
                                style={{
                                  padding: "0.625rem 1rem",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                GST Number
                              </th>
                              <th
                                style={{
                                  padding: "0.625rem 1rem",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.75rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                modified On
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {entity.ledgerNameList.map((item, i) => (
                              <tr
                                key={i}
                                style={{
                                  borderBottom:
                                    i < entity.ledgerNameList.length - 1
                                      ? "1px solid #f3f4f6"
                                      : "none",
                                  background: i % 2 === 0 ? "#fff" : "#fafafa",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "0.625rem 1rem",
                                    color: "#9ca3af",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    width: "36px",
                                  }}
                                >
                                  {i + 1}
                                </td>
                                {/* <td style={{ padding: "0.625rem 1rem" }}>
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#1d4ed8",
                                      fontSize: "0.8125rem",
                                    }}
                                  >
                                    {item.entityId || "—"}
                                  </span>
                                </td> */}
                                <td
                                  style={{
                                    padding: "0.625rem 1rem",
                                    color: "#374151",
                                  }}
                                >
                                  {item.companyName || "—"}
                                </td>
                                <td
                                  style={{
                                    padding: "0.625rem 1rem",
                                    fontWeight: 600,
                                    color: "#111827",
                                  }}
                                >
                                  {item.ledgerName || "—"}
                                </td>
                                <td
                                  style={{
                                    padding: "0.625rem 1rem",
                                    fontWeight: 600,
                                    color: "#111827",
                                  }}
                                >
                                  {item.gstNo || "—"}
                                </td>
                                <td
                                  style={{
                                    padding: "0.625rem 1rem",
                                    fontWeight: 600,
                                    // color: "#111827",
                                  }}
                                >
                                  {/* {item.createdOn || "—"} */}
                                  {item.createdOn
                                    ? new Date(
                                        item.createdOn,
                                      ).toLocaleDateString()
                                    : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                  {/* ── Uploaded Ledger Files (from ledgerList) ── */}
                  {!entity.ledgerList || entity.ledgerList.length === 0 ? (
                    (!entity.ledgerNameList ||
                      entity.ledgerNameList.length === 0) && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "3rem 1rem",
                          textAlign: "center",
                          background: "#f9fafb",
                          border: "1px dashed #d1d5db",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "3rem",
                            marginBottom: "1rem",
                            opacity: 0.5,
                          }}
                        >
                          📭
                        </div>
                        <div style={{ fontSize: "0.95rem", color: "#6b7280" }}>
                          No ledger data available
                        </div>
                      </div>
                    )
                  ) : (
                    <div>
                      <p
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: "0.625rem",
                        }}
                      >
                        Uploaded Files ({entity.ledgerList.length})
                      </p>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        {entity.ledgerList.map((ledger, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                              padding: "1rem",
                              background: "#f9fafb",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                            }}
                          >
                            <div style={{ fontSize: "1.5rem", flexShrink: 0 }}>
                              📄
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "0.95rem",
                                  fontWeight: 600,
                                  color: "#111827",
                                  marginBottom: "0.25rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={ledger.ledgerName}
                              >
                                {ledger.ledgerName ||
                                  ledger.fileName ||
                                  "Untitled"}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#6b7280",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {ledger.fileName || "—"}
                              </div>
                            </div>
                            <button
                              type="button"
                              style={{
                                flexShrink: 0,
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#3b82f6",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                              }}
                              onClick={async () => {
                                if (ledger.filePath) {
                                  const result = await downloadLedgerAction(
                                    ledger.filePath,
                                  );
                                  if (!result.success)
                                    toast.error(
                                      result.message ||
                                        "Failed to download file",
                                    );
                                }
                              }}
                              title="Download file"
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Uploaded Documents Section — only shown when entity.folderNo has a value */}
          {entity?.folderNo && (
            <UploadedDocumentsView entityPrimaryId={entity.folderNo} />
          )}

          <div className="form-actions">
            {/* Left side — View Logs */}
            {canViewlogs && (
              <button
                type="button"
                className="btn-view-logs"
                onClick={() => setViewLogsOpen(true)}
              >
                <Icon className="icon-listing" iconName="list" />
                View Logs
              </button>
            )}

            {/* Right side — all action buttons */}
            <div className="form-actions__right">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              {canUpdate && (
                <button
                  type="button"
                  className="btn-submit"
                  disabled={
                    !isEditing ||
                    isSubmitting ||
                    (entity.isVerified === true && entity.folderNo == null)
                  }
                  // enable={folderNo !== null}
                  onClick={(e) =>
                    handleUpdate(e, uploadedPaths, localGstEntries)
                  }
                  title={
                    entity.isVerified === true
                      ? "Entity already approved — cannot update"
                      : undefined
                  }
                >
                  {isSubmitting ? "Updating..." : "Update"}
                </button>
              )}
              {canDeficiency && (
                <button
                  type="button"
                  className="btn-submit-reject"
                  disabled={
                    isSubmitting ||
                    isActionLoading ||
                    entity.isVerified === true
                    // entity.folderNo === null
                  }
                  onClick={handleDeficiencyButtonClick}
                  title={
                    entity.isVerified === true
                      ? "Entity already approved — cannot mark deficiency"
                      : undefined
                  }
                >
                  {docDeficiencies.length > 0
                    ? `Deficiency (${docDeficiencies.length})`
                    : "Deficiency"}
                </button>
              )}
              {canApprove && (
                <button
                  type="button"
                  className="btn-submit-approve"
                  disabled={
                    isSubmitting ||
                    isActionLoading ||
                    entity.isVerified === true
                  }
                  onClick={handleApproveClick}
                  title={
                    entity.isVerified === true
                      ? "Already approved"
                      : "Review and approve documents"
                  }
                >
                  {isActionLoading
                    ? "Approving..."
                    : entity.isVerified === true
                      ? "Approved"
                      : "Approve"}
                </button>
              )}
            </div>
          </div>
        </form>
      </Modal>

      {/* Deficiency Review Modal — editable list, then submits to API */}
      <Modal
        isOpen={deficiencyReviewOpen}
        onClose={() => setDeficiencyReviewOpen(false)}
        title="Submit Deficiency"
        size="small"
      >
        <div className="doc-deficiency-review">
          {docDeficiencies.length === 0 ? (
            <div className="doc-deficiency-review__empty">
              <span className="doc-deficiency-review__empty-icon">⚠️</span>
              <p>No deficiencies added yet.</p>
              <p className="doc-deficiency-review__empty-hint">
                Close this and click ✗ on individual documents first.
              </p>
            </div>
          ) : (
            <>
              <p className="doc-deficiency-review__hint">
                Review and edit before submitting — {docDeficiencies.length}{" "}
                document{docDeficiencies.length > 1 ? "s" : ""}
              </p>
              <ul className="doc-deficiency-review__list">
                {docDeficiencies.map((d, i) => (
                  <li key={i} className="doc-deficiency-review__item">
                    <div className="doc-deficiency-review__item-header">
                      <span className="doc-deficiency-review__item-doc">
                        📄 {d.docName}
                      </span>
                      <button
                        type="button"
                        className="doc-deficiency-review__item-remove"
                        onClick={() =>
                          setDocDeficiencies((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                    <textarea
                      className="doc-deficiency-review__item-textarea"
                      rows={2}
                      value={d.reason}
                      onChange={(e) =>
                        setDocDeficiencies((prev) =>
                          prev.map((item, idx) =>
                            idx === i
                              ? { ...item, reason: e.target.value }
                              : item,
                          ),
                        )
                      }
                      placeholder="Edit reason..."
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="doc-deficiency-review__actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setDeficiencyReviewOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-submit-reject"
              disabled={
                docDeficiencies.length === 0 ||
                docDeficiencies.some((d) => !d.reason.trim()) ||
                isActionLoading
              }
              onClick={handleDeficiencySubmitToApi}
            >
              {isActionLoading ? "Submitting..." : "Submit Deficiency"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Approve Review Modal ─────────────────────────────────────────── */}
      <ApproveReviewModal
        isOpen={approveReviewOpen}
        onClose={() => setApproveReviewOpen(false)}
        documents={documents.map((doc) => {
          // For GST docs, inject gstId from localGstEntries by index
          const gstMatch = doc.key.match(/^gst_file_(\d+)$/);
          if (gstMatch) {
            const idx = parseInt(gstMatch[1], 10);
            const gstId = localGstEntries[idx]?.gstId ?? 0;
            return { ...doc, gstId };
          }
          return doc;
        })}
        approvedFilePaths={approvedFilePaths}
        viewedFilePaths={viewedFilePaths}
        allUploadedFilePaths={allUploadedFilePaths}
        pendingDocCount={pendingDocCount}
        allDocsViewed={allDocsViewed}
        approveReady={approveReady}
        hasAnyDeficiency={hasAnyDeficiency}
        isActionLoading={isActionLoading}
        handleViewDocument={handleViewDocument}
        handleDocApprove={handleDocApprove}
        handleApproveConfirm={handleApproveConfirm}
      />

      {/* ── View Logs Modal ───────────────────────────────────────────────── */}
      <ViewLogsModal
        isOpen={viewLogsOpen}
        onClose={() => setViewLogsOpen(false)}
        clientData={clientData}
      />

      {/* ── OTP Verification Modal for Aadhar ─────────────────────────────── */}
      <OtpVerificationModal
        isOpen={otpModalOpen}
        onClose={handleCloseOtpModal}
        onVerifySuccess={handleOtpVerifySuccess}
        adminEmail={adminEmail}
        fieldName={getCurrentFieldLabel()}
        fieldKey={currentAadharField}
        entityPrimaryId={entityPrimaryId}
      />
      {/* ── update the aadhar number with OTP Verification Modal for Aadhar ─────────────────────────────── */}
      <OtpVerificationModal
        isOpen={otpModalOpen}
        onClose={handleCloseOtpModal}
        onVerifySuccess={handleOtpVerifySuccess}
        adminEmail={adminEmail}
        fieldName={getCurrentFieldLabel()}
        fieldKey={currentAadharField}
        entityPrimaryId={entityPrimaryId}
      />

      {/* ── Document Viewer Modal ─────────────────────────────────────────── */}
      <DocumentViewer />
    </>
  );
};

export default EditClientModal;
